/**
 * tm/portal-ui/tds.tsx — FP 포털 · 고객 페이지 공용 부품 (2026-10-12)
 *  토스 TDS 의 짜임과 규칙(Top · ListHeader · ListRow 2줄 · Badge · ProgressBar · Button)을 따르되 부품은 직접 만든다(TDS 는 앱인토스 전용 라이선스).
 *  글꼴 Pretendard · 글자선: 카드 안 20px(화면 기준 32px) · 아이콘 칸 40 + 14 → 글자 시작 74 · 날짜 칸 84px 오른쪽 맞춤.
 *  색 = 뜻 하나: 빨강 지남 · 노랑 0~3일 · 파랑 4~7일 · 회색 8일+ · 청록 회신 기다림 · 초록 완료 · 진회색 중단.
 *  보험사 색 = 주 색(버튼 · 진행 막대 · 지금 단계 · 머리 띠) — 2026-10-13 지도사 「보험사 팀장님 색상으로」(전에는 머리 점 + 고른 줄 띠 두 곳만 · 7곳 검수)
 *   색상(色相)은 보험사 색 그대로 밝기만 맞춤 — 글자 NAVY(대비 4.5) · 칠 FILL(버튼 · 막대 · 지금 단계 · 3.0) · 맨 위 띠 · 점은 원래 색 · 빨강 지남 · 노랑 임박 배지는 그대로.
 */
import { Fragment, useEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import type { BadgeSpec, BadgeTone, LoanTerms, RoundResult } from './portalLogic';
import { wonText } from './portalLogic';

export const FONT = "'Pretendard Variable','Pretendard','Apple SD Gothic Neo',system-ui,-apple-system,'Malgun Gothic',sans-serif";
export const G = {
  g50: '#F9FAFB', g100: '#F2F4F6', g200: '#E5E8EB', g300: '#D1D6DB', g400: '#B0B8C1', g500: '#8B95A1',
  g600: '#6B7684', g700: '#4E5968', g800: '#333D4B', g900: '#191F28',
} as const;
export const BASE_NAVY = '#1B3A5C';
/** 주 색 — 팀장님 소속 보험사 색으로 바뀐다(setBrand · 2026-10-13 지도사 「FP 포털 · 고객 진행 안내도 소개한 보험사 팀장님 색상으로」) · 흰 글자 대비 4.5 이상으로 어둡게 맞춤 */
// eslint-disable-next-line import/no-mutable-exports
export let NAVY = BASE_NAVY;
/** 보험사 원래 색(머리 띠 · 점) — 글자에는 쓰지 않는다 */
// eslint-disable-next-line import/no-mutable-exports
export let BRAND_RAW = '';
function lumOf(hex: string): number | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return null;
  const n = parseInt(m[1], 16);
  const lin = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map(v => v / 255).map(v => (v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)));
  return 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2];
}
/** 같은 색상(色相) · 같은 채도에서 밝기만 낮춰 흰 바탕 대비 target 이상 — 15%씩 곱해 어둡게 하던 것은 주황이 갈색으로 바뀌었다
 *  (2026-10-13 지도사 「각 포인트 색은 각 보험사 색을 따라가야 해」) · 이미 넘으면 그 색 그대로 · 못 맞추면 '' */
export function shadeFor(hex: string, target: number): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return '';
  const n = parseInt(m[1], 16);
  const r = ((n >> 16) & 255) / 255, g = ((n >> 8) & 255) / 255, b = (n & 255) / 255;
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b), d = mx - mn;
  let h = 0; const l0 = (mx + mn) / 2; const sat = d === 0 ? 0 : d / (1 - Math.abs(2 * l0 - 1));
  if (d) { h = mx === r ? ((g - b) / d) % 6 : mx === g ? (b - r) / d + 2 : (r - g) / d + 4; h = (h * 60 + 360) % 360; }
  const toHex = (L: number) => {
    const c = (1 - Math.abs(2 * L - 1)) * sat, x = c * (1 - Math.abs(((h / 60) % 2) - 1)), m0 = L - c / 2;
    const [a1, b1, c1] = h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x] : h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x];
    return '#' + [a1, b1, c1].map(v => Math.round((v + m0) * 255).toString(16).padStart(2, '0')).join('').toUpperCase();
  };
  for (let L = l0; L >= 0.05; L -= 0.01) { const hx = toHex(L), lu = lumOf(hx); if (lu !== null && 1.05 / (lu + 0.05) >= target) return L === l0 ? '#' + m[1].toUpperCase() : hx; }
  return '';
}
/** 글자로 쓰는 주 색(대비 4.5) — 예전 이름 그대로(FP 포털 미리보기 머리 띠도 이것을 씀) */
export function readableOn(hex: string): string { return shadeFor(hex, 4.5); }
/** 칠하는 주 색(대비 3.0 · 글자 없는 그래픽) — 진행 막대 · 지금 단계 원 · 고른 줄 띠 · 옅은 바탕: 보험사 색에 가장 가깝게 · 흰 글자 버튼은 NAVY(4.5) */
// eslint-disable-next-line import/no-mutable-exports
export let FILL = BASE_NAVY;
/** 보험사 색으로 주 색 바꾸기 — 없거나 너무 밝아 못 쓰면 남색 · 화면을 그리기 전에 부른다
 *  NAVY = 글자(4.5) · FILL = 칠(3.0) · BRAND_RAW = 맨 위 띠 · 점(원래 색) — 셋 다 같은 색상(色相) */
export function setBrand(hex: string | null | undefined): string {
  const raw = hex && /^#?[0-9a-f]{6}$/i.test(hex) ? (hex.startsWith('#') ? hex : '#' + hex).toUpperCase() : '';
  NAVY = (raw && shadeFor(raw, 4.5)) || BASE_NAVY;
  FILL = (raw && shadeFor(raw, 3)) || NAVY;
  BRAND_RAW = raw;
  const st = typeof document !== 'undefined' ? document.getElementById('tds-brand-css') : null;
  const css = `.tds-in:focus,.tds-in:focus-within{box-shadow:inset 0 0 0 2px ${FILL};border-color:${FILL}!important}`;
  if (st) st.textContent = css;
  else if (typeof document !== 'undefined') { const e = document.createElement('style'); e.id = 'tds-brand-css'; e.textContent = css; document.head.appendChild(e); }
  return NAVY;
}
/** 맨 위 보험사 색 띠(4px) — 보험사 이름 없이 색만 */
export function BrandStrip() {
  return BRAND_RAW ? <div aria-hidden="true" style={{ height: 4, background: BRAND_RAW }} /> : null;
}
export const TEAL = '#0A7D74';
export const RED = '#D22030';
export const GREEN = '#05864F';
export const JIDO = '임지도사';

/** 긴 글 막기 — 업체 · 업무 · 단계 이름이 아무리 길어도 화면이 안 깨지게 n줄에서 「…」 (지도사 2026-10-12 「항상 줄이게」) */
export function clamp(n: number): CSSProperties {
  if (n <= 1) return { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 };
  return { display: '-webkit-box', WebkitLineClamp: n, WebkitBoxOrient: 'vertical', overflow: 'hidden', overflowWrap: 'anywhere', minWidth: 0 };
}

const TONE: Record<BadgeTone, [string, string]> = {
  elephant: ['#F2F4F6', '#4E5968'], blue: ['#E8F3FF', '#1B64DA'], yellow: ['#FFF3D6', '#9A5B00'],
  // 2026-10-12 2차 검수(색상): 지남 = 빨강 채움 · 흰 글자(적록 색약에서 노랑 「남음」과 밝기로 구분) · 멈춤 = 흰 바탕 + 테두리
  red: [RED, '#FFFFFF'], teal: ['#E1F8F5', '#08706A'], green: ['#E5F7EE', '#04764A'], stop: ['#FFFFFF', '#4E5968'],
};

/* ── 화면 폭 ── */
export function useWide(min = 1024): boolean {
  const [w, setW] = useState<boolean>(() => typeof window !== 'undefined' && window.innerWidth >= min);
  useEffect(() => {
    const on = () => setW(window.innerWidth >= min);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, [min]);
  return w;
}

/** 손가락으로 쓰는 기기인가(문자 · 카톡 앱 바로 열기) */
export function isTouch(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(pointer:coarse)').matches;
}

/* ── 아이콘(SVG 선) ── */
const PATHS: Record<string, string> = {
  cal: 'M5 5h14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2zM16 3v4M8 3v4M3 11h18',
  won: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM7.5 8.5l2 7 2.5-5.5 2.5 5.5 2-7M6.5 12h11',
  bld: 'M5.5 3h13A1.5 1.5 0 0 1 20 4.5v15a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 19.5v-15A1.5 1.5 0 0 1 5.5 3zM9 7h1M14 7h1M9 11h1M14 11h1M9 15h1M14 15h1M10 21v-3h4v3',
  clip: 'M9 2h6a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1zM16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2',
  up: 'M12 16V4M7 9l5-5 5 5M4 16v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3',
  doc: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6',
  brief: 'M4 7h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2zM16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2',
  award: 'M12 2a6 6 0 1 0 0 12 6 6 0 0 0 0-12zM8.5 13L7 22l5-3 5 3-1.5-9',
  sms: 'M21 12a8 8 0 0 1-11.6 7.1L4 20.5l1.4-4.6A8 8 0 1 1 21 12zM8.5 12h.01M12 12h.01M15.5 12h.01',
  search: 'M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14zM20 20l-3.5-3.5',
  check: 'M5 12.5l4.5 4.5L19 7.5',
  r: 'M9 5l7 7-7 7', d: 'M5 9l7 7 7-7', u: 'M5 15l7-7 7 7', back: 'M15 4l-8 8 8 8',
  folder: 'M3 6a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z',
  memo: 'M4 4h16v12H8l-4 4z',
  user: 'M12 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8zM4 21a8 8 0 0 1 16 0',
  send: 'M22 2L11 13M22 2l-7 20-4-9-9-4z',
  plus: 'M12 5v14M5 12h14',
  mic: 'M12 3a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V6a3 3 0 0 0-3-3zM5 11a7 7 0 0 0 14 0M12 18v3M8 21h8',
  img: 'M5 4h14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2zM8.5 10a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zM21 15l-5-5L5 20',
};
export type IconName = keyof typeof PATHS;

export function Icon({ name, size = 20, color = G.g700, sw = 1.8 }: { name: string; size?: number; color?: string; sw?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0, display: 'block' }}>
      <path d={PATHS[name] || PATHS.doc} />
    </svg>
  );
}

export function KakaoMark({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{ flexShrink: 0 }}>
      <path fill="#191919" d="M12 3C6.48 3 2 6.58 2 11c0 2.85 1.86 5.35 4.66 6.77l-.95 3.48c-.09.32.27.57.55.39l4.12-2.73c.53.06 1.07.09 1.62.09 5.52 0 10-3.58 10-8S17.52 3 12 3z" />
    </svg>
  );
}

/** 업무 아이콘 — 업무 이름으로 고른다(이모지 대신 선 아이콘) */
export function taskIcon(type: string): string {
  if (/정책자금|자금|대출|보증/.test(type)) return 'won';
  if (/재평가|부동산|감정/.test(type)) return 'bld';
  if (/벤처|인증|이노비즈|메인비즈|특허/.test(type)) return 'award';
  if (/고용|지원금|인력/.test(type)) return 'brief';
  return 'clip';
}

/* ── 배지 ── */
export function Badge({ spec }: { spec: BadgeSpec | null }) {
  if (!spec) return null;
  const [bg, fg] = TONE[spec.tone];
  return (
    <span title={spec.text} style={{ display: 'inline-block', lineHeight: '28px', height: 28, maxWidth: 160, padding: '0 10px', borderRadius: 14, background: bg, color: fg, fontSize: 15, fontWeight: 600, fontVariantNumeric: 'tabular-nums', boxShadow: spec.tone === 'stop' ? `inset 0 0 0 1px ${G.g400}` : undefined, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flexShrink: 0, boxSizing: 'border-box' }}>{spec.text}</span>
  );
}

/* ── 버튼 ── */
type BtnKind = 'fill' | 'weak' | 'kakao' | 'gray';
/** muted — 아직 덜 채움: 회색으로 보이되 누르면 빠진 칸을 알려 주게 눌림은 살려 둔다 */
export function Button({ children, kind = 'fill', h = 52, w, full = true, onClick, ariaLabel, muted }: { children: ReactNode; kind?: BtnKind; h?: number; w?: number; full?: boolean; onClick?: () => void; ariaLabel?: string; muted?: boolean }) {
  const look: Record<BtnKind, CSSProperties> = {
    // 꽉 찬 버튼 = 보험사 색 그대로에 가까운 FILL(대비 3.0) · 굵게(2026-10-27 지도사 「한화 색상이 아닌데 버튼이 나무색」 — 대비 4.5 로 어둡게 하니 주황이 갈색이 됐다 · 2026-10-13 「4.5 색」 결정을 바꿈)
    fill: { background: FILL, color: '#fff' }, weak: { background: FILL + '1A', color: NAVY },
    kakao: { background: '#FEE500', color: 'rgba(0,0,0,.88)' }, gray: { background: G.g100, color: G.g800 },
  };
  return (
    <button type="button" onClick={onClick} aria-label={ariaLabel} className="tds-btn"
      style={{ ...look[kind], ...(muted ? { background: G.g200, color: G.g700, whiteSpace: 'normal', lineHeight: 1.3, textAlign: 'center' } : {}), height: h, width: w ? w : full ? '100%' : undefined, padding: '0 14px', fontSize: muted ? 15 : h >= 56 ? 17 : h >= 48 ? 16 : 15, borderRadius: h >= 48 ? 14 : 12, fontWeight: !muted && (h >= 56 || kind === 'fill') ? 700 : 600, display: full ? 'flex' : 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, border: 'none', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0, fontFamily: FONT, boxSizing: 'border-box', maxWidth: '100%', overflow: 'hidden' }}>
      {typeof children === 'string' ? <span style={muted ? clamp(2) : clamp(1)}>{children}</span> : children}
    </button>
  );
}

/** 글자 연결 — 누르는 높이 44px */
export function TextLink({ children, color = NAVY, onClick, icon = 'r' }: { children: ReactNode; color?: string; onClick?: () => void; icon?: string }) {
  return (
    <button type="button" onClick={onClick} className="tds-btn"
      style={{ display: 'inline-flex', alignItems: 'center', gap: 2, minHeight: 44, padding: 0, fontSize: 15, fontWeight: 600, color, background: 'none', border: 'none', cursor: 'pointer', fontFamily: FONT }}>
      {children}<Icon name={icon} size={16} color={color} sw={2} />
    </button>
  );
}

/* ── 틀 ── */
export function Group({ children, pad = '4px 0 8px', m = '0 12px 12px' }: { children: ReactNode; pad?: string; m?: string }) {
  return <section style={{ background: '#fff', borderRadius: 20, margin: m, padding: pad, overflow: 'hidden' }}>{children}</section>;
}

export function ListHeader({ title, right }: { title: string; right?: ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 20px 8px' }}>
      <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: G.g900, ...clamp(1) }}>{title}</h2>
      {right !== undefined && <span style={{ fontSize: 15, color: G.g600, flexShrink: 0, marginLeft: 12 }}>{right}</span>}
    </div>
  );
}

export function Asset({ icon, ch, bg }: { icon?: string; ch?: string; bg?: string }) {
  return (
    <span aria-hidden="true" style={{ width: 40, height: 40, borderRadius: '50%', background: bg || G.g100, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 18, fontWeight: 700, color: G.g700 }}>
      {icon ? <Icon name={icon} /> : ch}
    </span>
  );
}

/** ListRow — arrow: undefined 칸 없음 · true › · false 빈 20px 칸(배지 열 맞춤)
 *  subRight — 배지를 둘째 줄 오른쪽에(제목이 배지에 밀려 잘리거나 꺾이지 않게 · 2026-10-12 검수)
 *  badge — 배지를 셋째 줄 글자 시작선에(둘째 줄이 길 때 — 둘째 줄이 배지에 밀려 「…」만 남던 것 · 2차 검수) */
export function ListRow({ left, title, sub, subRight, badge, right, arrow, onClick, dim, subColor, clip, pad = '12px 20px', selected }: {
  left?: ReactNode; title: ReactNode; sub?: ReactNode; subRight?: ReactNode; badge?: ReactNode; right?: ReactNode; arrow?: boolean; onClick?: () => void;
  dim?: boolean; subColor?: string; clip?: boolean; pad?: string; selected?: boolean;
}) {
  const body = (
    <>
      {left}
      <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
        <div style={{ fontSize: 17, fontWeight: 600, color: dim ? G.g600 : G.g900, lineHeight: 1.4, wordBreak: 'keep-all', ...clamp(clip ? 1 : 2) }}>{title}</div>
        {subRight ? (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 4 }}>
            <div style={{ flex: 1, fontSize: 15, color: subColor || G.g600, lineHeight: 1.45, paddingTop: 2, wordBreak: 'keep-all', ...clamp(clip ? 1 : 2) }}>{sub}</div>
            {subRight}
          </div>
        ) : sub && <div style={{ fontSize: 15, color: subColor || G.g600, lineHeight: 1.45, marginTop: 2, wordBreak: 'keep-all', ...clamp(clip ? 1 : 2) }}>{sub}</div>}
        {badge && <div style={{ marginTop: 6, display: 'flex' }}>{badge}</div>}
      </div>
      {right}
      {arrow !== undefined && <span style={{ width: 20, flexShrink: 0, display: 'flex', justifyContent: 'flex-end' }}>{arrow && <Icon name="r" size={18} color={G.g500} sw={2} />}</span>}
    </>
  );
  const style: CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 14, padding: pad, minHeight: 48, width: '100%', boxSizing: 'border-box',
    background: selected ? G.g100 : 'transparent',   // 고른 줄 = 옅은 바탕만(왼쪽 색 막대 ✕ · 2026-10-27 지도사 「클로드 특유 디자인」)
    border: 'none', fontFamily: FONT, cursor: onClick ? 'pointer' : 'default',
  };
  return onClick
    ? <button type="button" className="tds-row" onClick={onClick} style={style}>{body}</button>
    : <div style={style}>{body}</div>;
}

export function Divider({ full }: { full?: boolean }) {
  return <div style={{ height: 1, background: G.g200, margin: full ? '0 20px' : '0 20px 0 74px' }} />;
}

export function ProgressBar({ pct, pend = 0, color = FILL, label }: { pct: number; pend?: number; color?: string; label?: string }) {
  return (
    <div role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={label}
      style={{ height: 6, borderRadius: 3, background: G.g200, overflow: 'hidden', display: 'flex' }}>
      <div style={{ height: '100%', width: pct + '%', background: color }} />
      {pend > 0 && <div style={{ height: '100%', width: pend + '%', background: color + '40' }} />}
    </div>
  );
}

export function Para({ children, size = 16, color = G.g700 }: { children: ReactNode; size?: number; color?: string }) {
  return <div style={{ fontSize: size, lineHeight: 1.6, color, wordBreak: 'keep-all', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{children}</div>;
}

/** 긴 글 — n줄까지 보이고 넘치면 「더 보기」(질문 · 안내 · 상담 기록처럼 내용이 중요한 글) */
/** 글 속 http(s) 주소 → 「링크 열기 (도메인)」 — 긴 주소가 세 줄을 차지하고 누를 수도 없던 것(2026-10-12 2차 검수) */
function linkify(t: string): ReactNode {
  const parts = t.split(/(https?:\/\/[^\s]+)/g);
  if (parts.length === 1) return t;
  return parts.map((x, i) => {
    if (!/^https?:\/\//.test(x)) return x;
    let host = '';
    try { host = new URL(x).hostname.replace(/^www\./, ''); } catch { host = ''; }
    return <a key={i} href={x} target="_blank" rel="noopener noreferrer" style={{ color: '#1B64DA', fontWeight: 600, textDecoration: 'underline', overflowWrap: 'anywhere' }}>링크 열기{host ? ` (${host})` : ''}</a>;
  });
}

export function MoreText({ children, lines = 4, size = 16, color = G.g700 }: { children: ReactNode; lines?: number; size?: number; color?: string }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [over, setOver] = useState(false);
  useEffect(() => { const el = ref.current; if (el && !open) setOver(el.scrollHeight > el.clientHeight + 1); }, [children, open, lines]);
  return (
    <div>
      <div ref={ref} style={{ fontSize: size, lineHeight: 1.6, color, wordBreak: 'keep-all', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', ...(open ? {} : clamp(lines)) }}>{typeof children === 'string' ? linkify(children) : children}</div>
      {(over || open) && <TextLink color={G.g700} icon={open ? 'u' : 'd'} onClick={() => setOpen(o => !o)}>{open ? '접기' : '더 보기'}</TextLink>}
    </div>
  );
}

export function Top({ title, subTop, subBottom, extra, padT = 8, padB = 20, side = 32 }: { title: ReactNode; subTop?: ReactNode; subBottom?: ReactNode; extra?: ReactNode; padT?: number; padB?: number; side?: number }) {
  return (
    <div style={{ background: '#fff', padding: `${padT}px ${side}px ${padB}px` }}>
      {subTop && <div style={{ fontSize: 15, fontWeight: 500, color: G.g600, marginBottom: 6, ...(typeof subTop === 'string' ? clamp(1) : {}) }}>{subTop}</div>}
      <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700, color: G.g900, lineHeight: 1.35, wordBreak: 'keep-all', ...clamp(2) }}>{title}</h1>
      {subBottom && <div style={{ fontSize: 17, fontWeight: 600, color: G.g800, marginTop: 8, lineHeight: 1.45, wordBreak: 'keep-all', ...clamp(3) }}>{subBottom}</div>}
      {extra}
    </div>
  );
}

export function AsOf({ text, mt = 6 }: { text: string; mt?: number }) {
  return <div style={{ fontSize: 15, color: G.g600, marginTop: mt }}>{text}</div>;
}

export function AppBar({ onBack }: { onBack: () => void }) {
  return (
    <div style={{ height: 56, background: '#fff', display: 'flex', alignItems: 'center', padding: '0 8px' }}>
      <button type="button" onClick={onBack} aria-label="처음 화면으로" className="tds-btn"
        style={{ width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none', cursor: 'pointer' }}>
        <Icon name="back" size={24} color={G.g900} sw={2} />
      </button>
    </div>
  );
}

/** 단계 줄 — 40px 칸 가운데 28px 원 · 글자 시작선 = 업무 제목 · 날짜 칸 112px(「10/16(금)까지」 · 2026-10-13) */
export function StepRow({ n, name, note, date, state, bold }: { n: number; name: string; note?: string; date: ReactNode; state: 'done' | 'miss' | 'now' | 'wait' | 'plan'; bold?: boolean }) {
  const c = state === 'wait' ? TEAL : FILL;
  // 2026-10-28 지도사 「1, 2, 3이 보여야 단계라고 느껴질 듯」 — 모든 단계에 번호 · 상태는 원 색(끝 초록 · 지금 주황 · 기다림 청록 · 결과 아님 회색 · 앞으로 연회색)
  const num = (bg: string, fg: string, label?: string) => <span aria-label={label} style={{ width: 28, height: 28, borderRadius: '50%', background: bg, color: fg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700 }}>{n}</span>;
  const dot = state === 'done' ? num(GREEN, '#fff', `${n}단계 끝`)
    : state === 'miss' ? num(G.g500, '#fff', `${n}단계 결과 아님`)
    : state === 'plan' ? num(G.g100, G.g700)
    : num(c, '#fff', `${n}단계 지금`);
  const bg = state === 'wait' ? '#F2FBFA' : state === 'now' ? FILL + '12' : '#fff';
  const noteColor = state === 'wait' ? TEAL : state === 'now' ? NAVY : G.g600;
  // 날짜 굵기 — 지금 단계 · 📌 공고 마감만 굵게, 앞으로 올 날짜는 보통(6개가 모두 굵어 시선이 흩어졌음 · 2026-10-13 검수)
  const dateStyle: CSSProperties = state === 'done' || state === 'miss' ? { fontWeight: 400, color: G.g600 } : bold ? { fontWeight: 700, color: G.g900 } : state === 'now' || state === 'wait' ? { fontWeight: 600, color: G.g800 } : { fontWeight: 400, color: G.g700 };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 20px', minHeight: 48, background: bg }}>
      <span style={{ width: 40, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>{dot}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 16, lineHeight: 1.4, fontWeight: state === 'now' || state === 'wait' ? 700 : state === 'plan' ? 400 : 500, color: state === 'plan' ? G.g700 : state === 'done' || state === 'miss' ? G.g800 : G.g900, wordBreak: 'keep-all', ...clamp(2) }}>{name}</div>
        {note && <div style={{ fontSize: 15, color: noteColor, marginTop: 2, lineHeight: 1.45, wordBreak: 'keep-all', ...clamp(2) }}>{note}</div>}
      </div>
      <span style={{ width: 92, flexShrink: 0, display: 'flex', justifyContent: 'flex-end', fontSize: 15, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', ...dateStyle }}>{date}</span>
    </div>
  );
}

/** 업무 카드 요약 — 진행 막대 아래 두 줄: 지금(굵게) · 최근 소식(회색 이름표 + 글) (2026-10-27 지도사 「디자인 조잡하고 클로드 냄새난다」)
 *  예전: 문장 + 왼쪽 색줄 상자(최근 소식) + 떠 있는 글자 연결(단계 접기) — 모양이 셋이라 산만했다 → 같은 15px 두 줄 · 상자 없음
 *  이름표 칸(표)은 휴대폰 폭에서 내용이 두세 줄로 꺾여 안 씀 — 이름표는 글 앞에 회색으로
 *  고객 진행 안내 · 팀장님 포털이 같이 쓴다 · 대출 결과(TermsBox)는 children 으로 아래에 */
export function TaskSummary({ pct, bar, now, wait, news, children, pad = '0 20px 16px 74px' }: {
  pct: number; bar: boolean; now?: string; wait?: boolean; news?: { day: string; text: string; label: string } | null; children?: ReactNode; pad?: string;
}) {
  const line: CSSProperties = { fontSize: 15, lineHeight: 1.5, wordBreak: 'keep-all', ...clamp(2) };
  return (
    <div style={{ padding: pad }}>
      {bar && <div style={{ marginBottom: 12 }}><ProgressBar pct={pct} label="진행" /></div>}
      {now && <div style={{ ...line, fontWeight: 600, color: wait ? TEAL : G.g900 }}>{now}</div>}
      {news && news.text && <div data-day={news.day} style={{ ...line, color: G.g800, marginTop: now ? 4 : 0 }}><span style={{ color: G.g600 }}>최근 소식 {news.label}</span>{'\u2002'}{news.text}</div>}
      {children}
    </div>
  );
}

/** 카드 맨 아래 단계 펼치기 줄 — 가는 선 위 가운데 한 줄(48px) · 펼치면 단계 목록 아래로 내려와 「단계 접기」 */
export function FoldFooter({ open, count, onClick }: { open: boolean; count: number; onClick: () => void }) {
  return (
    <div style={{ margin: '0 20px', borderTop: `1px solid ${G.g200}` }}>
      <button type="button" className="tds-btn" aria-expanded={open} onClick={onClick}
        style={{ width: '100%', minHeight: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, padding: 0, background: 'none', border: 'none', cursor: 'pointer', fontFamily: FONT, fontSize: 15, fontWeight: 600, color: G.g700 }}>
        {open ? '단계 접기' : `${count}단계 모두 보기`}<Icon name={open ? 'u' : 'd'} size={16} color={G.g700} sw={2} />
      </button>
    </div>
  );
}

/** 대출 결과(2026-10-14 지도사 「얼마 · 언제 · 금리 · 예정인지 확정인지 변경될 수 있는지 — 고객 · 팀장님이 궁금해해」)
 *  머리 「대출 결과」 + 상태(대략 예상 · 확정 · 실행 완료) · 칸 금액 / 금리 / 실행 · 대략 예상이면 「바뀔 수 있어요」 한 줄 */
export function TermsBox({ terms, label = '' }: { terms: LoanTerms; label?: string }) {
  // 2026-10-14 모의 검토(토스 디자이너 · 인지공학 역할) — 상태는 배지 대신 한 문장 · 금액을 가장 크게 · 얼마 → 언제 → 금리 순 · 안내 글자 15px 짙게
  const W = '일월화수목금토';
  const day = terms.when ? `${+terms.when.slice(5, 7)}월 ${+terms.when.slice(8, 10)}일(${W.charAt(new Date(+terms.when.slice(0, 4), +terms.when.slice(5, 7) - 1, +terms.when.slice(8, 10)).getDay())})` : '';
  const st = terms.state === 'done' ? { t: '대출이 실행됐어요', c: '#047857' } : terms.state === 'set' ? { t: '조건이 확정됐어요', c: '#1D4ED8' } : { t: '이 정도로 예상돼요', c: '#B45309' };
  const rows: [string, string][] = [];
  if (terms.lender) rows.push(['은행', terms.lender]);   // 어느 은행(기관)에서 — 2026-10-28 지도사
  if (day) rows.push([terms.state === 'done' ? '실행일' : '실행 예정', day]);
  if (terms.rate) rows.push(['금리', `연 ${terms.rate}%`]);
  return (
    <div style={{ marginTop: 10, padding: '14px 16px', borderRadius: 14, background: G.g50, minWidth: 0, maxWidth: '100%', boxSizing: 'border-box' }}>   {/* 긴 기관 이름도 칸 안에서 「…」(320px 검사) */}
      <div style={{ fontSize: 14, color: G.g700, ...clamp(1) }}>{label ? `${label} 대출 결과` : '대출 결과'}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: st.c, marginTop: 2 }}>{st.t}</div>
      {terms.amount != null && <div style={{ fontSize: 26, fontWeight: 800, color: G.g900, marginTop: 6, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em' }}>{wonText(terms.amount)}</div>}
      {rows.length > 0 && <div style={{ display: 'grid', gridTemplateColumns: '72px minmax(0,1fr)', columnGap: 8, rowGap: 4, marginTop: 6 }}>
        {rows.map(([k, v]) => <Fragment key={k}><span style={{ fontSize: 15, color: G.g700 }}>{k}</span><b style={{ fontSize: 15, color: G.g900, fontVariantNumeric: 'tabular-nums', wordBreak: 'keep-all' }}>{v}</b></Fragment>)}
      </div>}
      {terms.state !== 'done' && <div style={{ fontSize: 15, color: '#4B5563', marginTop: 8, lineHeight: 1.5, wordBreak: 'keep-all' }}>{terms.state === 'guess' ? '금융기관 심사에 따라 바뀔 수 있어요' : '금융기관에서 확정한 조건이에요'}</div>}
    </div>
  );
}

/** 선정 안 됨 대책 — ① 왜 안 됐는지 ② 보완할 점 ③ 다음 신청 · 대출 결과(TermsBox)와 같은 회색 판 · 같은 글자 크기(2026-10-27 지도사 「고객이 납득가능하도록」) */
export function RoundResultBox({ r, label = '' }: { r: RoundResult; label?: string }) {
  const rows: [string, string][] = [['이유', r.reason], ['보완할 점', r.improve], ['다음 신청', r.next]];
  return (
    <div style={{ marginTop: 10, padding: '14px 16px', borderRadius: 14, background: G.g50, minWidth: 0, maxWidth: '100%', boxSizing: 'border-box' }}>
      {/* 「선정되지 않았어요」는 바로 위 「지금은」 줄이 말한다 — 여기선 이유 · 보완 · 다음만(같은 말 세 번 X) */}
      <div style={{ fontSize: 14, color: G.g700, ...clamp(1) }}>{label ? `${label.replace(/\s*신청$/, '')} 심사 결과` : '심사 결과'}</div>
      <div style={{ display: 'grid', gridTemplateColumns: '72px minmax(0,1fr)', columnGap: 8, rowGap: 6, marginTop: 6 }}>
        {rows.filter(([, v]) => v).map(([k, v]) => <Fragment key={k}><span style={{ fontSize: 15, color: G.g700 }}>{k}</span><span style={{ fontSize: 15, color: G.g900, lineHeight: 1.5, wordBreak: 'keep-all', whiteSpace: 'pre-wrap' }}>{v}</span></Fragment>)}
      </div>
    </div>
  );
}

/** 시트 — 휴대폰은 아래에서 올라오는 시트 · PC(가로 768 이상)는 화면 가운데 창(2026-10-27 지도사 「팝업이 너무 바닥에 붙었다」)
 *  고객 진행 안내 · 팀장님 포털이 같이 쓴다 · 바깥을 누르면 닫힘 */
export function Sheet({ label, onClose, children, maxWidth = 560, z = 55 }: { label: string; onClose: () => void; children: ReactNode; maxWidth?: number; z?: number }) {
  const pc = useWide(768);
  return (
    <div role="dialog" aria-modal="true" aria-label={label} onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.32)', display: 'flex', alignItems: pc ? 'center' : 'flex-end', justifyContent: 'center', zIndex: z, fontFamily: FONT, padding: pc ? 24 : 0, boxSizing: 'border-box' }}>
      <div onClick={e => e.stopPropagation()}
        style={{ width: '100%', maxWidth, maxHeight: pc ? 'calc(100vh - 48px)' : '86vh', overflowY: 'auto', background: '#fff', borderRadius: pc ? 20 : '20px 20px 0 0',
          padding: pc ? 24 : '24px 24px calc(20px + env(safe-area-inset-bottom, 0px))', boxSizing: 'border-box' }}>
        {children}
      </div>
    </div>
  );
}

/* ── 알림(3초) — alert 대신 ── */
export function useToast(bottom = 24): [ReactNode, (msg: string) => void] {
  const [msg, setMsg] = useState('');
  useEffect(() => {
    if (!msg) return undefined;
    const t = window.setTimeout(() => setMsg(''), 3000);
    return () => window.clearTimeout(t);
  }, [msg]);
  const node = msg
    ? <div role="status" style={{ position: 'fixed', left: '50%', bottom: `calc(${bottom}px + env(safe-area-inset-bottom, 0px))`, transform: 'translateX(-50%)', maxWidth: 'calc(100% - 32px)', background: G.g900, color: '#fff', fontSize: 15, lineHeight: 1.5, padding: '12px 16px', borderRadius: 14, zIndex: 50, fontFamily: FONT, textAlign: 'center' }}>{msg}</div>
    : null;
  return [node, setMsg];
}

/** 페이지 공통 CSS(한 번) — 누름 표시 · 포커스 테두리 · 밝은 화면 고정 */
export function usePortalStyles(): void {
  useEffect(() => {
    document.documentElement.style.setProperty('color-scheme', 'only light');
    document.body.style.background = G.g100;
    if (!document.getElementById('tds-brand-css')) setBrand(BRAND_RAW || null);   // 입력 칸 포커스 테두리(주 색)
    if (document.getElementById('tds-portal-css')) return;
    const s = document.createElement('style');
    s.id = 'tds-portal-css';
    s.textContent = `.tds-root,.tds-root *{box-sizing:border-box;-webkit-tap-highlight-color:transparent}
.tds-root{overflow-wrap:anywhere;line-height:1.45;font-variant-numeric:tabular-nums;font-family:${FONT};letter-spacing:-0.01em;color:${G.g900};background:${G.g100};min-height:100vh;-webkit-font-smoothing:antialiased}
.tds-btn:focus-visible,.tds-row:focus-visible{outline:2px solid #1B64DA;outline-offset:2px}
.fp-search:focus-within{box-shadow:inset 0 0 0 2px #1B64DA!important}
.tds-root div{text-wrap:pretty}
.tds-row:hover{background:${G.g50}!important}.tds-btn:active,.tds-row:active{opacity:.85}`;
    document.head.appendChild(s);
  }, []);
}
